/**
 * Shopify E-commerce Platform Tracker
 * Auto-detects and tracks e-commerce events from Shopify stores
 */
import BaseEcommerceTracker from './base';

export default class ShopifyTracker extends BaseEcommerceTracker {
  constructor(b2mTracker, platformName) {
    super(b2mTracker, platformName);
    
    // Shopify-specific selectors
    this.selectors = {
      // Product page
      productDetail: '.product, .product-single, [data-product], .product-template',
      productName: '.product__title, .product-single__title, h1.title, [data-product-title]',
      productPrice: '.product__price, .product-single__price, [data-product-price], .price__regular',
      productComparePrice: '.product__price--compare, .price__compare, [data-compare-price]',
      productImage: '.product__media img, .product-single__photo img, [data-product-image]',
      productVendor: '.product__vendor, [data-product-vendor]',
      productType: '.product__type, [data-product-type]',
      
      // Add to cart
      addToCartButton: '[data-add-to-cart], .product-form__submit, .add-to-cart, button[name="add"]',
      quantityInput: '[data-quantity-input], input[name="quantity"], .quantity__input',
      variantSelect: '[data-variant-select], select[name="id"], .variant-selector',
      
      // Cart
      cartPage: '.cart, [data-cart], .template-cart',
      cartItems: '.cart-item, .cart__item, [data-cart-item]',
      cartTotal: '.cart__total, [data-cart-total], .totals__total-value',
      cartItemRemove: '.cart-item__remove, [data-cart-remove], .cart__remove',
      
      // Mini cart / drawer
      cartDrawer: '.cart-drawer, .mini-cart, [data-cart-drawer]',
      
      // Checkout
      checkoutPage: '.checkout, [data-checkout]',
      
      // Collection/Category pages
      collectionPage: '.collection, [data-collection], .template-collection',
      productList: '.collection-products, .product-grid, [data-product-grid]',
      productCard: '.product-card, .grid__item, [data-product-card]',
      
      // Search
      searchInput: '[data-search-input], input[name="q"], .search__input',
      searchResults: '.search-results, [data-search-results], .template-search',
      
      // Wishlist (common apps)
      wishlistButton: '.wishlist-btn, [data-wishlist], .add-to-wishlist'
    };
    
    // Shopify URL patterns
    this.urlPatterns = {
      product: ['/products/'],
      category: ['/collections/'],
      cart: ['/cart'],
      checkout: ['/checkouts/', '/checkout'],
      orderConfirmation: ['/thank_you', '/orders/'],
      search: ['/search'],
      account: ['/account']
    };
  }

  /**
   * Initialize Shopify-specific tracking
   */
  init() {
    super.init();
    
    // Setup Shopify-specific tracking
    this.setupShopifyEventListeners();
    this.setupAddToCartTracking();
    this.setupCartUpdateTracking();
    this.setupVariantChangeTracking();
    this.setupSearchTracking();
    
    // Listen for Shopify Analytics events if available
    this.interceptShopifyAnalytics();
    
    console.log('[B2M Shopify] ✅ Shopify-specific tracking initialized');
  }

  /**
   * Get page type from URL - Shopify specific
   */
  getPageType(path) {
    const url = path.toLowerCase();
    
    for (const [type, patterns] of Object.entries(this.urlPatterns)) {
      for (const pattern of patterns) {
        if (url.includes(pattern)) {
          return type;
        }
      }
    }
    
    return 'other';
  }

  /**
   * Auto-track product page
   */
  autoTrackProductPage() {
    // Try to get product data from Shopify's built-in data
    const product = this.getShopifyProductData();
    
    if (product) {
      this.trackViewItem([product], {
        page_type: 'product_detail',
        source: 'shopify_data'
      });
    } else {
      // Fallback to DOM extraction
      this.waitForElement(this.selectors.productDetail, (container) => {
        const productData = this.extractShopifyProduct(container);
        if (productData.item_id || productData.item_name) {
          this.trackViewItem([productData], {
            page_type: 'product_detail',
            source: 'dom_extraction'
          });
        }
      });
    }
  }

  /**
   * Auto-track collection/category page
   */
  autoTrackCategoryPage() {
    this.waitForElement(this.selectors.productList, (container) => {
      const items = this.extractProductList(container);
      const collectionName = this.getCollectionName();
      
      if (items.length > 0) {
        this.trackViewItemList(items, collectionName, {
          page_type: 'collection',
          source: 'auto_detect'
        });
      }
      
      // Setup click tracking
      this.setupProductCardClicks(container);
    });
  }

  /**
   * Auto-track cart page
   */
  autoTrackCartPage() {
    // Try Shopify cart API first
    this.fetchShopifyCart().then(cart => {
      if (cart && cart.items) {
        const items = cart.items.map(item => this.normalizeShopifyCartItem(item));
        this.trackViewCart(items, cart.total_price / 100, cart.currency || 'USD', {
          page_type: 'cart',
          source: 'shopify_api'
        });
      }
    }).catch(() => {
      // Fallback to DOM
      this.waitForElement(this.selectors.cartPage, (container) => {
        const items = this.extractCartItems(container);
        const total = this.extractCartTotal(container);
        
        if (items.length > 0) {
          this.trackViewCart(items, total, 'USD', {
            page_type: 'cart',
            source: 'dom_extraction'
          });
        }
      });
    });
  }

  /**
   * Auto-track checkout page
   */
  autoTrackCheckoutPage() {
    // Shopify checkout is often on a different domain
    // Try to get data from Shopify.checkout object
    if (window.Shopify?.checkout) {
      const checkout = window.Shopify.checkout;
      const items = (checkout.line_items || []).map(item => this.normalizeShopifyCheckoutItem(item));
      
      this.trackBeginCheckout(items, checkout.total_price / 100, checkout.currency, {
        page_type: 'checkout',
        source: 'shopify_checkout'
      });
    }
  }

  /**
   * Auto-track order confirmation
   */
  autoTrackOrderConfirmation() {
    // Shopify provides order data on thank you page
    if (window.Shopify?.checkout) {
      const checkout = window.Shopify.checkout;
      
      this.trackPurchase({
        transaction_id: checkout.order_id || checkout.token,
        value: checkout.total_price / 100,
        currency: checkout.currency,
        tax: (checkout.total_tax || 0) / 100,
        shipping: (checkout.shipping_rate?.price || 0) / 100,
        items: (checkout.line_items || []).map(item => this.normalizeShopifyCheckoutItem(item))
      }, {
        page_type: 'order_confirmation',
        source: 'shopify_checkout'
      });
    }
  }

  /**
   * Auto-track search page
   */
  autoTrackSearchPage() {
    const searchTerm = new URLSearchParams(window.location.search).get('q');
    
    if (searchTerm) {
      this.waitForElement(this.selectors.searchResults, (container) => {
        const items = this.extractProductList(container);
        
        this.trackSearch(searchTerm, {
          results_count: items.length,
          source: 'auto_detect'
        });
        
        if (items.length > 0) {
          this.trackViewItemList(items, `Search: ${searchTerm}`, {
            search_term: searchTerm
          });
        }
      });
    }
  }

  // ============================================
  // Shopify-Specific Event Tracking
  // ============================================

  /**
   * Setup Shopify event listeners
   */
  setupShopifyEventListeners() {
    // Listen for Shopify theme events
    document.addEventListener('cart:add', (e) => {
      if (e.detail) {
        this.handleCartAdd(e.detail);
      }
    });
    
    document.addEventListener('cart:update', (e) => {
      if (e.detail) {
        this.handleCartUpdate(e.detail);
      }
    });
    
    // Common Shopify theme event patterns
    document.addEventListener('theme:cart:add', (e) => this.handleCartAdd(e.detail));
    document.addEventListener('theme:cart:change', (e) => this.handleCartUpdate(e.detail));
    
    // Shopify sections API events
    document.addEventListener('shopify:section:load', () => {
      this.detectCurrentPage();
    });
  }

  /**
   * Setup add to cart tracking
   */
  setupAddToCartTracking() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.addToCartButton);
      if (!button) return;
      
      // Get product form
      const form = button.closest('form[action*="/cart/add"]');
      if (!form) return;
      
      // Extract product data
      const product = this.extractProductFromForm(form);
      
      if (product.item_id || product.item_name) {
        this.trackAddToCart([product], product.price * (product.quantity || 1), 'USD', {
          source: 'button_click'
        });
      }
    });

    // Also intercept form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.action?.includes('/cart/add')) return;
      
      const product = this.extractProductFromForm(form);
      
      if (product.item_id || product.item_name) {
        this.trackAddToCart([product], product.price * (product.quantity || 1), 'USD', {
          source: 'form_submit'
        });
      }
    });
  }

  /**
   * Setup cart update tracking
   */
  setupCartUpdateTracking() {
    // Intercept cart API calls
    this.interceptFetch();
  }

  /**
   * Setup variant change tracking
   */
  setupVariantChangeTracking() {
    document.addEventListener('change', (e) => {
      const variantSelect = e.target.closest(this.selectors.variantSelect);
      if (!variantSelect) return;
      
      const selectedVariant = this.getSelectedVariant();
      if (selectedVariant) {
        this.b2mTracker.track('variant_change', {
          ecommerce_platform: this.platformName,
          variant_id: selectedVariant.id,
          variant_title: selectedVariant.title,
          variant_price: selectedVariant.price / 100,
          source: 'variant_select'
        });
      }
    });
  }

  /**
   * Setup search tracking
   */
  setupSearchTracking() {
    const searchInput = document.querySelector(this.selectors.searchInput);
    if (!searchInput) return;
    
    const searchForm = searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        const searchTerm = searchInput.value.trim();
        if (searchTerm) {
          this.trackSearch(searchTerm, {
            source: 'form_submit'
          });
        }
      });
    }
  }

  /**
   * Intercept Shopify Analytics if available
   */
  interceptShopifyAnalytics() {
    // Check for Shopify Analytics
    if (window.ShopifyAnalytics) {
      const originalTrack = window.ShopifyAnalytics.lib?.track;
      if (originalTrack) {
        window.ShopifyAnalytics.lib.track = (eventName, data) => {
          this.handleShopifyAnalyticsEvent(eventName, data);
          return originalTrack.call(window.ShopifyAnalytics.lib, eventName, data);
        };
      }
    }
  }

  /**
   * Handle Shopify Analytics events
   */
  handleShopifyAnalyticsEvent(eventName, data) {
    switch (eventName) {
      case 'Viewed Product':
        if (data.productId) {
          this.trackViewItem([{
            item_id: data.productId,
            item_name: data.productTitle,
            price: data.productPrice,
            item_brand: data.productVendor,
            item_category: data.productType
          }], { source: 'shopify_analytics' });
        }
        break;
      case 'Added Product':
        this.trackAddToCart([{
          item_id: data.productId,
          item_name: data.productTitle,
          price: data.productPrice,
          quantity: data.quantity || 1
        }], data.cartTotal, 'USD', { source: 'shopify_analytics' });
        break;
    }
  }

  /**
   * Intercept fetch calls to cart API
   */
  interceptFetch() {
    const originalFetch = window.fetch;
    
    window.fetch = async (url, options) => {
      const response = await originalFetch(url, options);
      
      // Clone response to read body
      const clonedResponse = response.clone();
      
      try {
        // Check if this is a cart API call
        if (typeof url === 'string') {
          if (url.includes('/cart/add')) {
            const data = await clonedResponse.json();
            this.handleCartAddResponse(data);
          } else if (url.includes('/cart/change') || url.includes('/cart/update')) {
            const data = await clonedResponse.json();
            this.handleCartUpdateResponse(data);
          }
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
      
      return response;
    };
  }

  /**
   * Handle cart add response
   */
  handleCartAddResponse(data) {
    if (data.items) {
      const items = data.items.map(item => this.normalizeShopifyCartItem(item));
      this.trackAddToCart(items, null, 'USD', { source: 'cart_api' });
    } else if (data.id) {
      // Single item added
      this.trackAddToCart([this.normalizeShopifyCartItem(data)], null, 'USD', { source: 'cart_api' });
    }
  }

  /**
   * Handle cart update response
   */
  handleCartUpdateResponse(data) {
    // This could be a quantity change or removal
    // Compare with previous cart state if available
  }

  /**
   * Handle cart add event
   */
  handleCartAdd(detail) {
    if (detail.product || detail.item) {
      const item = detail.product || detail.item;
      this.trackAddToCart([this.normalizeShopifyProduct(item)], null, 'USD', {
        source: 'theme_event'
      });
    }
  }

  /**
   * Handle cart update event
   */
  handleCartUpdate(detail) {
    // Track cart updates
  }

  // ============================================
  // Data Extraction Methods
  // ============================================

  /**
   * Get product data from Shopify's global object
   */
  getShopifyProductData() {
    // Shopify stores product data in various places
    const product = window.product || 
                    window.ShopifyAnalytics?.meta?.product ||
                    window.meta?.product;
    
    if (product) {
      return {
        item_id: product.id?.toString() || product.gid,
        item_name: product.title,
        price: product.price / 100,
        item_brand: product.vendor,
        item_category: product.type,
        item_variant: product.variants?.[0]?.title,
        currency: window.Shopify?.currency?.active || 'USD'
      };
    }
    
    // Try to get from JSON-LD
    const jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (jsonLd) {
      try {
        const data = JSON.parse(jsonLd.textContent);
        if (data['@type'] === 'Product') {
          return {
            item_id: data.sku || data.productID,
            item_name: data.name,
            price: parseFloat(data.offers?.price) || 0,
            item_brand: data.brand?.name,
            currency: data.offers?.priceCurrency || 'USD'
          };
        }
      } catch (e) {}
    }
    
    return null;
  }

  /**
   * Extract product from Shopify DOM
   */
  extractShopifyProduct(container) {
    const product = {
      item_id: null,
      item_name: null,
      price: 0,
      item_brand: null,
      item_category: null,
      item_variant: null,
      currency: window.Shopify?.currency?.active || 'USD'
    };
    
    // Get from data attributes
    product.item_id = container.dataset?.productId || 
                      container.querySelector('[data-product-id]')?.dataset?.productId;
    
    // Get name
    const nameEl = container.querySelector(this.selectors.productName);
    product.item_name = nameEl?.textContent?.trim();
    
    // Get price
    const priceEl = container.querySelector(this.selectors.productPrice);
    if (priceEl) {
      product.price = this.parseShopifyPrice(priceEl.textContent);
    }
    
    // Get vendor
    const vendorEl = container.querySelector(this.selectors.productVendor);
    product.item_brand = vendorEl?.textContent?.trim();
    
    // Get type
    const typeEl = container.querySelector(this.selectors.productType);
    product.item_category = typeEl?.textContent?.trim();
    
    return product;
  }

  /**
   * Extract product from form
   */
  extractProductFromForm(form) {
    const product = this.getShopifyProductData() || {};
    
    // Get variant ID from form
    const variantInput = form.querySelector('input[name="id"], select[name="id"]');
    if (variantInput) {
      product.variant_id = variantInput.value;
    }
    
    // Get quantity
    const quantityInput = form.querySelector(this.selectors.quantityInput);
    product.quantity = parseInt(quantityInput?.value) || 1;
    
    // Get selected variant details
    const selectedVariant = this.getSelectedVariant();
    if (selectedVariant) {
      product.item_variant = selectedVariant.title;
      product.price = selectedVariant.price / 100;
    }
    
    return product;
  }

  /**
   * Extract products from list
   */
  extractProductList(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    const items = [];
    
    productCards.forEach((card, index) => {
      const product = this.extractShopifyProduct(card);
      product.index = index;
      
      // Try to get from card data attributes
      if (!product.item_id) {
        const productLink = card.querySelector('a[href*="/products/"]');
        if (productLink) {
          const match = productLink.href.match(/\/products\/([^/?]+)/);
          if (match) {
            product.item_id = match[1]; // Use handle as ID
          }
        }
      }
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract cart items from DOM
   */
  extractCartItems(container) {
    const cartItems = container.querySelectorAll(this.selectors.cartItems);
    const items = [];
    
    cartItems.forEach((item, index) => {
      const product = {
        item_id: item.dataset?.variantId || item.dataset?.productId,
        item_name: item.querySelector('.cart-item__name, [data-cart-item-name]')?.textContent?.trim(),
        price: this.parseShopifyPrice(item.querySelector('.cart-item__price, [data-cart-item-price]')?.textContent),
        quantity: parseInt(item.querySelector(this.selectors.quantityInput)?.value) || 1,
        index
      };
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract cart total
   */
  extractCartTotal(container) {
    const totalEl = container.querySelector(this.selectors.cartTotal);
    if (totalEl) {
      return this.parseShopifyPrice(totalEl.textContent);
    }
    return 0;
  }

  /**
   * Get collection name
   */
  getCollectionName() {
    // Try window.collection
    if (window.collection?.title) {
      return window.collection.title;
    }
    
    // Try from URL
    const match = window.location.pathname.match(/\/collections\/([^/]+)/);
    if (match) {
      return match[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    
    // Try from page title
    return document.querySelector('h1')?.textContent?.trim() || 'Collection';
  }

  /**
   * Get selected variant
   */
  getSelectedVariant() {
    // Check window.product.variants
    if (window.product?.variants) {
      const variantId = document.querySelector('input[name="id"], select[name="id"]')?.value;
      if (variantId) {
        return window.product.variants.find(v => v.id.toString() === variantId);
      }
      return window.product.variants[0];
    }
    return null;
  }

  /**
   * Fetch cart from Shopify API
   */
  async fetchShopifyCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  /**
   * Parse Shopify price string
   */
  parseShopifyPrice(priceStr) {
    if (!priceStr) return 0;
    
    // Shopify prices might be in cents or formatted
    const cleaned = priceStr.toString().replace(/[^\d.,]/g, '');
    const price = parseFloat(cleaned.replace(',', '.'));
    
    // If price seems to be in cents (> 1000 for most products), divide by 100
    return price > 10000 ? price / 100 : price;
  }

  /**
   * Normalize Shopify cart item
   */
  normalizeShopifyCartItem(item) {
    return {
      item_id: item.variant_id?.toString() || item.id?.toString(),
      item_name: item.product_title || item.title,
      item_brand: item.vendor,
      item_variant: item.variant_title,
      price: (item.price || item.final_price) / 100,
      quantity: item.quantity || 1,
      item_category: item.product_type
    };
  }

  /**
   * Normalize Shopify checkout item
   */
  normalizeShopifyCheckoutItem(item) {
    return {
      item_id: item.variant_id?.toString() || item.id?.toString(),
      item_name: item.title,
      item_brand: item.vendor,
      item_variant: item.variant_title,
      price: item.price / 100,
      quantity: item.quantity || 1
    };
  }

  /**
   * Normalize Shopify product object
   */
  normalizeShopifyProduct(product) {
    return {
      item_id: product.id?.toString() || product.variant_id?.toString(),
      item_name: product.title || product.product_title,
      item_brand: product.vendor,
      item_category: product.type || product.product_type,
      item_variant: product.variant_title,
      price: (product.price || 0) / 100,
      quantity: product.quantity || 1
    };
  }

  /**
   * Setup product card click tracking
   */
  setupProductCardClicks(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    
    productCards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        // Don't track if clicking on add to cart
        if (e.target.closest(this.selectors.addToCartButton)) return;
        
        const product = this.extractShopifyProduct(card);
        product.index = index;
        
        if (product.item_id || product.item_name) {
          this.trackSelectItem([product], {
            source: 'product_card_click'
          });
        }
      });
    });
  }

  /**
   * Wait for element
   */
  waitForElement(selector, callback, timeout = 5000) {
    const element = document.querySelector(selector);
    
    if (element) {
      callback(element);
      return;
    }
    
    const observer = new MutationObserver((mutations, obs) => {
      const el = document.querySelector(selector);
      if (el) {
        obs.disconnect();
        callback(el);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    setTimeout(() => observer.disconnect(), timeout);
  }
}
